import { defineAsyncComponent } from 'vue'

import type { DrawerFeatureTwin, FeatureConfig } from 'src/models'
import { withBase } from 'src/utils/base-url'

/**
 * Agent profile types for user-facing chat features.
 * - 'default': General-purpose Digital Twins assistant
 * - 'climate_risk': Specialized Climate Risk Nexus assistant
 * - 'flow_config': Flow Configuration Copilot
 */
export type ChatAgentProfile = 'default' | 'climate_risk' | 'flow_config'

/**
 * Configuration for the Chat Feature.
 * Extends DrawerFeatureTwin with AI-specific settings.
 *
 * DTDL Model: dtmi:com:arcadis:app:ChatFeature;1
 */
export interface ChatFeatureTwin extends DrawerFeatureTwin {
  /** Agent profile to use for this chat feature */
  agentProfile?: ChatAgentProfile
  /** Custom title for the chat drawer header */
  chatTitle?: string
  /** Placeholder text for the message input field */
  inputPlaceholder?: string
  /**
   * Initial suggestions shown before the first message.
   * Comma-separated string as defined in DTDL.
   * Use the `initialSuggestions` computed in ChatFeatureDrawer for the parsed array.
   */
  initialSuggestions?: string
  /** Whether to show suggestion chips after AI responses (default: true) */
  showSuggestions?: boolean
  /**
   * Custom instructions to include in the AI system prompt.
   * Use this to provide context specific to this environment or use case.
   */
  customInstructions?: string
  /**
   * When enabled, the current dashboard configuration (queries, parameters)
   * will be passed to the AI for context-aware responses.
   */
  includeDashboardContext?: boolean
  /**
   * When enabled, information about selected twins/items
   * will be passed to the AI for context-aware responses.
   */
  includeSelectionContext?: boolean
  /**
   * Shows an AI chat toggle button in the app header for quick access regardless of twin selection.
   * Default: true when this feature is present.
   */
  showHeaderButton?: boolean
  /** When enabled, summarised tile result snapshots are sent in custom_context. */
  includeQueryResults?: boolean
  /** Cap for sample rows per tile (default 10, max 50). */
  maxQueryResultRows?: number
  /**
   * When enabled, conversations are private to the current user.
   * When disabled (default), all users in the environment can see all conversations.
   */
  privateConversations?: boolean
}

const featureConfig: FeatureConfig = {
  drawer: {
    drawerComponent: defineAsyncComponent(() => import('./ChatFeatureDrawer.vue')),
    icon: `img:${withBase('/icons/sparkle.svg')}`,
    label: 'AI Chat',
    width: 600,
    independentOfSelection: true
  }
}

export default featureConfig
